import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import api from '../api/api';
import {
  AlertCircle, ArrowRight, CheckCircle2, ClipboardCheck, CloudUpload,
  Database, FileSpreadsheet,  Loader2, RefreshCw, Search, Sparkles, Upload, X,
} from 'lucide-react';

const HEADER_MAP = {
  shop: ['shop', 'branch', 'storename', 'store'],
  date: ['date', 'reportdate', 'transactiondate'],
  time: ['time', 'transactiontime'],
  code: ['couponno', 'couponnumber', 'coupon', 'code', 'couponcode', 'discountcode', 'redeemcode'],
  customerName: ['customername', 'customer', 'customerfullname', 'name'],
  productCode: ['productcode', 'sku', 'productsku'],
  productName: ['productname', 'product', 'itemname'],
  retailPrice: ['retailprice', 'price', 'retail'],
  quantity: ['quantity', 'qty'],
  sales: ['sales', 'sale'],
  couponAmount: ['couponamount'],
  couponDiscount: ['coupondiscount'],
  grossSale: ['grosssale', 'grosssales'],
  netSales: ['netsales', 'netsale'],
};

const DISPLAY_COLUMNS = [
  ['shop', 'Shop'], ['date', 'Date'], ['time', 'Time'], ['code', 'Coupon No.'],
  ['customerName', 'Customer Name'], ['productCode', 'Product Code'],
  ['productName', 'Product Name'], ['retailPrice', 'Retail Price'],
  ['quantity', 'Quantity'], ['couponAmount', 'Coupon Amount'], ['couponDiscount', 'Coupon Discount'],
];

const normalizeHeader = (value) => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
const normalizeCode = (value) => String(value ?? '').trim().replace(/\s+/g, '').toUpperCase();
const isValidCode = (value) => {
  const code = normalizeCode(value);
  return code.length >= 3 && code.length <= 100;
};
const parseMoney = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(String(value).replace(/,/g, '').replace(/%/g, '').trim());
  return Number.isFinite(number) ? number : null;
};
const parseDateOnly = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const [, month, day, year] = slashMatch;
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};
const combineDateTime = (dateValue, timeValue) => {
  const dateOnly = parseDateOnly(dateValue);
  if (!dateOnly) return null;
  const timeText = String(timeValue ?? '').trim();
  return timeText ? `${dateOnly} ${timeText}` : `${dateOnly} 00:00:00`;
};
const getExtension = (name = '') => name.split('.').pop()?.toLowerCase() || '';

const StatCard = ({ label, value, note, icon: Icon, tone = 'dark' }) => {
  const themes = {
    dark: {
      card: 'border-slate-200 bg-slate-950 shadow-[0_18px_45px_rgba(15,23,42,0.12)]',
      glow: 'bg-[#F6C453]/12', iconBox: 'border-[#F6C453]/20 bg-[#F6C453]/10 text-[#F6C453]',
      accent: 'text-[#F6C453]', value: 'text-white', note: 'text-slate-400',
      line: 'bg-[#F6C453]', eyebrow: 'text-[#F6C453]',
    },
    green: {
      card: 'border-emerald-100 bg-gradient-to-br from-white via-white to-emerald-50/70 shadow-[0_18px_45px_rgba(16,185,129,0.08)]',
      glow: 'bg-emerald-300/15', iconBox: 'border-emerald-200 bg-emerald-50 text-emerald-600',
      accent: 'text-emerald-600', value: 'text-slate-950', note: 'text-slate-500',
      line: 'bg-emerald-500', eyebrow: 'text-emerald-600',
    },
  };
  const theme = themes[tone] || themes.dark;
  return (
    <div className={`group relative min-h-[155px] overflow-hidden rounded-[26px] border px-5 py-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_55px_rgba(15,23,42,0.14)] sm:px-6 sm:py-6 ${theme.card}`}>
      <div className={`absolute -right-14 -top-14 h-40 w-40 rounded-full blur-3xl transition-transform duration-500 group-hover:scale-110 ${theme.glow}`} />
      <div className="absolute right-5 top-5 opacity-[0.05] transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110">
        <Icon size={88} strokeWidth={1.2} />
      </div>
      <div className="relative z-10 flex h-full items-center justify-between gap-5">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`h-1.5 w-1.5 rounded-full ${theme.line}`} />
            <p className={`text-[9px] font-black uppercase tracking-[0.2em] ${theme.eyebrow}`}>{label}</p>
          </div>
          <div className="mt-4 flex items-end gap-3">
            <p className={`text-[42px] font-black leading-none tracking-[-0.05em] sm:text-[48px] ${theme.value}`}>{value}</p>
            <div className={`mb-1 hidden text-[9px] font-extrabold uppercase tracking-[0.16em] sm:block ${theme.accent}`}>Live</div>
          </div>
          <p className={`mt-3 max-w-[360px] text-[11px] font-medium leading-5 ${theme.note}`}>{note}</p>
        </div>
        <div className={`flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[18px] border shadow-sm transition-all duration-300 group-hover:scale-105 ${theme.iconBox}`}>
          <Icon size={27} strokeWidth={2.2} />
        </div>
      </div>
      <div className={`absolute bottom-0 left-0 h-[3px] w-full opacity-80 ${theme.line}`} />
    </div>
  );
};

const StatusBadge = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[9px] font-extrabold text-emerald-700">
    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
    Redeemed
  </span>
);

const RedeemCode = () => {
  const inputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [workbook, setWorkbook] = useState(null);
  const [sheetNames, setSheetNames] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [columns, setColumns] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);

  const defaultMapping = {
    shop: '', date: '', time: '', code: '', customerName: '', productCode: '',
    productName: '', retailPrice: '', quantity: '', sales: '', couponAmount: '',
    couponDiscount: '', grossSale: '', netSales: '',
  };
  const [mapping, setMapping] = useState(defaultMapping);

  const [register, setRegister] = useState([]);
  const [registerMeta, setRegisterMeta] = useState({ total: 0, redeemed: 0 });

  const [search, setSearch] = useState('');
  const [loadingRegister, setLoadingRegister] = useState(false);
  const [reading, setReading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [notice, setNotice] = useState(null);

  const showNotice = (type, message) => setNotice({ type, message });

  const autoDetect = (headers) => {
    const detected = {};
    Object.entries(HEADER_MAP).forEach(([key, candidates]) => {
      const match = headers.find((header) => candidates.includes(normalizeHeader(header)));
      detected[key] = match || '';
    });
    return detected;
  };

  const loadSheet = (book, sheetName) => {
    const worksheet = book?.Sheets?.[sheetName];
    if (!worksheet) return;

    const matrix = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '', raw: false });
    const rows = matrix.filter((row) => Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== ''));

    if (!rows.length) {
      setColumns([]);
      setPreviewRows([]);
      setMapping((prev) => ({ ...prev, code: '' }));
      return;
    }

    const headers = rows[0].map((value, index) => String(value ?? '').trim() || `Column ${index + 1}`);
    const data = rows.slice(1).map((row, index) => {
      const record = { __row: index + 2 };
      headers.forEach((header, columnIndex) => { record[header] = row[columnIndex] ?? ''; });
      return record;
    });

    setColumns(headers);
    setPreviewRows(data);
    setMapping(autoDetect(headers));
  };

  const clearSelection = () => {
    setSelectedFile(null); setWorkbook(null); setSheetNames([]);
    setSelectedSheet(''); setColumns([]); setPreviewRows([]);
    setMapping(defaultMapping);
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!['xlsx', 'xls', 'csv'].includes(getExtension(file.name))) {
      return showNotice('error', 'Only XLSX, XLS, and CSV files are allowed.');
    }
    setReading(true);
    setNotice(null);
    try {
      const buffer = await file.arrayBuffer();
      const book = XLSX.read(buffer, { type: 'array', cellDates: false });
      if (!book.SheetNames.length) throw new Error('No worksheet was found.');
      setSelectedFile(file);
      setWorkbook(book);
      setSheetNames(book.SheetNames);
      setSelectedSheet(book.SheetNames[0]);
      loadSheet(book, book.SheetNames[0]);
      showNotice('success', `${file.name} has been read successfully.`);
    } catch (error) {
      clearSelection();
      showNotice('error', error?.message || 'The Excel file could not be read.');
    } finally {
      setReading(false);
    }
  };

  const importPreviewRows = useMemo(() => {
    return previewRows.map((row) => {
      const code = normalizeCode(row[mapping.code]);
      if (!isValidCode(code)) return null;
      return {
        shop: mapping.shop ? String(row[mapping.shop] ?? '').trim() : '',
        date: mapping.date ? String(row[mapping.date] ?? '').trim() : '',
        time: mapping.time ? String(row[mapping.time] ?? '').trim() : '',
        code,
        customerName: mapping.customerName ? String(row[mapping.customerName] ?? '').trim() : '',
        productCode: mapping.productCode ? String(row[mapping.productCode] ?? '').trim() : '',
        productName: mapping.productName ? String(row[mapping.productName] ?? '').trim() : '',
        retailPrice: mapping.retailPrice ? parseMoney(row[mapping.retailPrice]) : null,
        quantity: mapping.quantity ? parseMoney(row[mapping.quantity]) : null,
        sales: mapping.sales ? parseMoney(row[mapping.sales]) : null,
        couponAmount: mapping.couponAmount ? parseMoney(row[mapping.couponAmount]) : null,
        couponDiscount: mapping.couponDiscount ? parseMoney(row[mapping.couponDiscount]) : null,
        grossSale: mapping.grossSale ? parseMoney(row[mapping.grossSale]) : null,
        netSales: mapping.netSales ? parseMoney(row[mapping.netSales]) : null,
        reportDate: mapping.date ? parseDateOnly(row[mapping.date]) : null,
        redeemedAt: mapping.date ? combineDateTime(row[mapping.date], mapping.time ? row[mapping.time] : '') : null,
        sourceSheet: selectedSheet,
        sourceRow: row.__row,
      };
    }).filter(Boolean);
  }, [previewRows, mapping, selectedSheet]);

  const loadRegister = useCallback(async () => {
    setLoadingRegister(true);
    try {
      const response = await api.get('/api/coupons/imported-redeemed', {
        params: { search: search.trim(), limit: 250, page: 1 },
      });
      const data = response.data || {};
      setRegister(Array.isArray(data.records) ? data.records : []);
      
      // Latest file import ke stats set karein
      setRegisterMeta({
        total: Number(data.latestVerified ?? 0),
        redeemed: Number(data.latestTotalImported ?? 0),
      });
    } catch (error) {
      showNotice('error', error?.response?.data?.message || 'Imported records could not be loaded.');
    } finally {
      setLoadingRegister(false);
    }
  }, [search]);

  useEffect(() => {
    loadRegister();
  }, [loadRegister]);

  const handleImport = async () => {
    if (!selectedFile) return showNotice('error', 'Please select an Excel file first.');
    if (!mapping.code) return showNotice('error', 'Please select the Coupon No. / Code column.');
    if (!importPreviewRows.length) return showNotice('error', 'No valid Coupon No. was found in the file.');

    setImporting(true);
    try {
      const payload = { sourceFile: selectedFile.name, codes: importPreviewRows };
      const response = await api.post('/api/coupons/import-redeemed', payload);
      const result = response.data || {};

      showNotice('success', `${result.sessionVerifiedCount ?? 0} codes verified. ${result.sessionUnverifiedCount ?? 0} unverified codes skipped.`);

      // Direct current batch result state me update karein
      setRegisterMeta({
        total: Number(result.latestVerified ?? result.sessionVerifiedCount ?? 0),
        redeemed: Number(result.latestTotalImported ?? importPreviewRows.length ?? 0),
      });

      await loadRegister(); 
    } catch (error) {
      showNotice('error', error?.response?.data?.message || 'Redeemed codes could not be imported.');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="w-full flex-1 p-4 sm:p-6 lg:p-8">
      <div className="mx-auto flex w-full max-w-7xl flex-col gap-6">
        
        {/* HEADER */}
        <section className="relative overflow-hidden rounded-[28px] bg-slate-950 px-5 py-6 text-white shadow-[0_20px_60px_rgba(15,23,42,0.13)] sm:px-7 sm:py-7 lg:px-8">
          <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#F6C453]/15 blur-3xl" />
          <div className="absolute -bottom-28 left-[40%] h-72 w-72 rounded-full bg-blue-500/10 blur-3xl" />
          <div className="relative grid gap-7 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.07] px-3 py-1.5 text-[9px] font-extrabold uppercase tracking-[0.2em] text-[#F6C453]">
                <Sparkles size={12} /> Redemption Operations
              </div>
              <div className="mt-4 flex items-start gap-4">
                <div className="hidden h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.07] text-[#F6C453] sm:flex">
                  <Database size={25} />
                </div>
                <div>
                  <h1 className="text-[26px] font-black leading-tight tracking-[-0.03em] sm:text-3xl lg:text-[34px]">
                    Redeemed Code Import
                  </h1>
                </div>
              </div>
            </div>
            <div className="flex w-full max-w-[330px] items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] p-3.5 backdrop-blur-sm lg:w-[300px]">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F6C453] text-slate-950 shadow-lg">
                <FileSpreadsheet size={21} />
              </div>
              <div>
                <p className="text-[9px] font-extrabold uppercase tracking-[0.16em] text-slate-400">Report format</p>
                <p className="mt-1 text-sm font-extrabold text-white">Coupon No. based</p>
                <p className="mt-0.5 text-[10px] text-slate-400">XLSX · XLS · CSV</p>
              </div>
            </div>
          </div>
        </section>

        {/* TOP STAT CARDS (LATEST FILE STATS) */}
        <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <StatCard label="Verified codes" value={registerMeta.total} note="Invalid Codes from imported file" icon={Database} tone="dark" />
          <StatCard label=" Total Generated codes " value={registerMeta.redeemed} note="Total valid codes uploaded in the latest report." icon={CheckCircle2} tone="green" />
        </section>

        {/* UPLOAD SECTION */}
        <section className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-[0_10px_36px_rgba(15,23,42,0.045)] sm:p-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-900 text-[#F6C453]">
                  <Upload size={14} />
                </span>
                <div>
                  <h2 className="mt-0.5 text-base font-black text-slate-900">Upload redemption report</h2>
                </div>
              </div>
              <p className="mt-3 max-w-xl text-[11px] leading-5 text-slate-500">
                In your provided report structure, <b>Coupon No.</b> is used as the primary redemption key.
              </p>
            </div>
            {selectedFile && (
              <button type="button" onClick={clearSelection} className="shrink-0 rounded-xl border border-slate-200 px-3 py-2 text-[10px] font-extrabold text-slate-600 hover:bg-slate-50">Clear</button>
            )}
          </div>

          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
            onDrop={(e) => { e.preventDefault(); setDragActive(false); handleFile(e.dataTransfer.files?.[0]); }}
            className={`group relative mt-5 flex min-h-[285px] w-full flex-col items-center justify-center overflow-hidden rounded-[22px] border border-dashed px-5 py-8 text-center transition-all duration-300 ${dragActive ? 'border-[#E6B94A] bg-amber-50/70 shadow-[0_0_0_5px_rgba(246,196,83,0.08)]' : 'border-slate-200 bg-gradient-to-b from-slate-50 to-white hover:border-amber-300 hover:shadow-[0_12px_38px_rgba(15,23,42,0.055)]'}`}
          >
            <div className="flex h-16 w-16 items-center justify-center rounded-[20px] bg-slate-900 text-[#F6C453] shadow-[0_14px_35px_rgba(15,23,42,0.12)] transition-transform duration-300 group-hover:-translate-y-1">
              {reading ? <Loader2 size={27} className="animate-spin" /> : <CloudUpload size={27} />}
            </div>
            <p className="mt-5 text-sm font-black text-slate-900 sm:text-base">{selectedFile ? selectedFile.name : 'Drop Excel file here'}</p>
            <p className="mt-1.5 max-w-lg text-[11px] leading-5 text-slate-400">{selectedFile ? `${(selectedFile.size / 1024).toFixed(1)} KB · ${sheetNames.length} worksheet(s)` : 'Supported: XLSX, XLS, and CSV'}</p>
            <span className="mt-5 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-[10px] font-extrabold text-white shadow-lg shadow-slate-200">
              <Upload size={13} /> Select Report File
            </span>
            <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ''; }} className="hidden" />
          </button>

          <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-3">
            <MiniInfo label="File" value={selectedFile?.name || 'Waiting for file'} />
            <MiniInfo label="Sheet" value={selectedSheet || '—'} />
            <MiniInfo label="Rows" value={previewRows.length || '—'} />
          </div>

          <button type="button" onClick={handleImport} disabled={importing || reading || !selectedFile || !mapping.code || !importPreviewRows.length} className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-[11px] font-extrabold text-white shadow-[0_12px_25px_rgba(15,23,42,0.14)] transition-all hover:-translate-y-0.5 hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0">
            {importing ? <Loader2 size={15} className="animate-spin" /> : <Database size={15} />}
            {importing ? 'Saving to database...' : 'Import Redeemed Codes'}
            {!importing && <ArrowRight size={14} />}
          </button>
          
        </section>

        {/* NOTICE */}
        {notice && (
          <div className={`flex items-start gap-3 rounded-2xl border px-4 py-3 shadow-sm ${notice.type === 'error' ? 'border-red-200 bg-red-50 text-red-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
            {notice.type === 'error' ? <AlertCircle size={17} className="mt-0.5 shrink-0" /> : <CheckCircle2 size={17} className="mt-0.5 shrink-0" />}
            <p className="flex-1 text-[11px] font-semibold leading-5">{notice.message}</p>
            <button type="button" onClick={() => setNotice(null)} className="rounded-lg p-1 hover:bg-black/5"><X size={15} /></button>
          </div>
        )}

        {/* REPORT PREVIEW */}
        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_10px_36px_rgba(15,23,42,0.045)]">
          <div className="flex flex-col gap-3 border-b border-slate-100 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-[#F6C453]"><FileSpreadsheet size={17} /></div>
              <div><h2 className="mt-0.5 text-base font-black text-slate-900">Report preview</h2></div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[9px] font-extrabold text-slate-500">{previewRows.length || 0} rows</span>
              {mapping.code && <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-[9px] font-extrabold text-emerald-700">Coupon: {mapping.code}</span>}
            </div>
          </div>
          {!previewRows.length ? <EmptyPreview /> : (
            <div className="overflow-x-auto">
              <table className="min-w-[1250px] w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    <th className="px-4 py-3 text-[8px] font-extrabold uppercase tracking-[0.14em] text-slate-400">Row</th>
                    {DISPLAY_COLUMNS.map(([key, label]) => (
                      <th key={key} className={`px-4 py-3 text-[8px] font-extrabold uppercase tracking-[0.14em] ${key === 'code' ? 'text-emerald-600' : 'text-slate-400'}`}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {previewRows.slice(0, 10).map((row) => (
                    <tr key={row.__row} className="transition hover:bg-slate-50/70">
                      <td className="px-4 py-3 text-[9px] font-black text-slate-400">{row.__row}</td>
                      {DISPLAY_COLUMNS.map(([key]) => {
                        const sourceColumn = mapping[key];
                        const value = sourceColumn ? row[sourceColumn] : '';
                        const codeCell = key === 'code';
                        return (
                          <td key={key} className="max-w-[190px] px-4 py-3 text-[10px] font-semibold text-slate-600">
                            {codeCell && value ? (
                              <span className="inline-flex rounded-lg bg-slate-900 px-2 py-1 font-mono text-[9px] font-extrabold tracking-wide text-[#F6C453]">{normalizeCode(value)}</span>
                            ) : (
                              <span className="block truncate" title={String(value ?? '')}>{String(value ?? '—') || '—'}</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* DATABASE HISTORY */}
        <section className="overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_10px_36px_rgba(15,23,42,0.045)]">
          <div className="flex flex-col gap-4 border-b border-slate-100 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600"><ClipboardCheck size={18} /></div>
              <div>
                <p className="text-[9px] font-extrabold uppercase tracking-[0.17em] text-emerald-500">Database history</p>
                <h2 className="mt-0.5 text-base font-black text-slate-900">Imported redemption register</h2>
              </div>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <div className="relative min-w-[240px]">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') loadRegister(); }} placeholder="Search coupon..." className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-[10px] font-semibold text-slate-700 outline-none transition focus:border-[#E6B94A] focus:bg-white focus:ring-2 focus:ring-amber-50" />
              </div>
              <button type="button" onClick={loadRegister} disabled={loadingRegister} className="inline-flex h-10 items-center justify-center gap-2 rounded-xl bg-slate-900 px-3 text-[10px] font-extrabold text-white hover:bg-slate-800 disabled:opacity-50">
                {loadingRegister ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
              </button>
            </div>
          </div>
          {!register.length ? (
            <div className="flex flex-col items-center justify-center px-5 py-14 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"><Database size={22} /></div>
              <p className="mt-4 text-sm font-black text-slate-700">No imported records loaded</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-[1250px] w-full text-left">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/80">
                    {['Coupon No.', 'Shop', 'Customer Name', 'Product Code', 'Product Name', 'Date', 'Time', 'Retail Price', 'Quantity', 'Coupon Amount', 'Coupon Discount', 'Status'].map((label) => (
                      <th key={label} className="px-4 py-3 text-[8px] font-extrabold uppercase tracking-[0.15em] text-slate-400">{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {register.map((item) => (
                    <tr key={item.id || item.code} className="transition hover:bg-slate-50/70">
                      <td className="px-4 py-3"><span className="inline-flex rounded-lg bg-slate-900 px-2.5 py-1.5 font-mono text-[9px] font-extrabold tracking-wide text-[#F6C453]">{item.code}</span></td>
                      <td className="px-4 py-3 text-[10px] font-bold text-slate-700">{item.shop || '—'}</td>
                      <td className="max-w-[160px] px-4 py-3 text-[10px] font-semibold text-slate-600"><span className="block truncate" title={item.customerName}>{item.customerName || '—'}</span></td>
                      <td className="px-4 py-3 text-[10px] font-semibold text-slate-600">{item.productCode || '—'}</td>
                      <td className="max-w-[180px] px-4 py-3"><p className="truncate text-[10px] font-bold text-slate-700" title={item.productName}>{item.productName || '—'}</p></td>
                      <td className="px-4 py-3 text-[10px] font-semibold text-slate-600">{item.date || '—'}</td>
                      <td className="px-4 py-3 text-[10px] font-semibold text-slate-600">{item.time || '—'}</td>
                      <td className="px-4 py-3 text-[10px] font-semibold text-slate-600">{item.retailPrice ?? '—'}</td>
                      <td className="px-4 py-3 text-[10px] font-semibold text-slate-600">{item.quantity ?? '—'}</td>
                      <td className="px-4 py-3 text-[10px] font-semibold text-slate-600">{item.couponAmount ?? '—'}</td>
                      <td className="px-4 py-3 text-[10px] font-semibold text-slate-600">{item.couponDiscount ?? '—'}</td>
                      <td className="px-4 py-3"><StatusBadge /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

      </div>
    </div>
  );
};

const MiniInfo = ({ label, value }) => (
  <div className="min-w-0 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
    <p className="text-[8px] font-extrabold uppercase tracking-[0.15em] text-slate-400">{label}</p>
    <p className="mt-1 truncate text-[10px] font-bold text-slate-700" title={value}>{value}</p>
  </div>
);

const EmptyPreview = () => (
  <div className="flex flex-col items-center justify-center px-5 py-14 text-center">
    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400"><FileSpreadsheet size={23} /></div>
    <p className="mt-4 text-sm font-black text-slate-700">Report preview is not ready.</p>
    <p className="mt-1 max-w-lg text-[11px] leading-5 text-slate-400">Please upload the redemption report first.</p>
  </div>
);

export default RedeemCode;